import React, { useEffect, useMemo, useState, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { showSuccess, showError } from "toast/toast";
import { Row } from "@tanstack/react-table";
import { RotationalProducts, Product } from "types";
import DataTable from "components/DataTable";
import PaginationControls from "components/PaginationControls";
import { ChangeEvent, KeyboardEvent } from "react";
import TablePaginationControls from "components/TableandmobilePaginationcontrol"
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import { ViewIconButton, EditIconButton, DeleteIconButton } from "components/DatatablesActionIcons";
import DeleteConfirmModal from "components/ToolTips/DeleteConfirmModal";

import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";

import {
  ExportAsExcel,
} from "@siamf/react-export";

import { Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import GlobalFilter from "components/GlobalFilter";
import { fetchParentHierarchy } from "utils/userDataFetcher";
import { getAuthCookie } from "../../utils/authCookies";
import { useModuleActionPermissions } from "hooks/useModuleActionPermissions";
import { MODULE_PERMISSION_NAMES } from "utils/moduleActionPermissions";

interface ExportRow {
  "First Name": string;
  "Last Name": string;
  "Email": string;
  "Phone": string;
  "Status": string;
}

function fuzzyTextFilter<TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: string
) {
  const raw = row.getValue(columnId);
  const value = (typeof raw === "boolean"
    ? raw
      ? "Active"
      : "Inactive"
    : String(raw)
  ).toLowerCase();

  return value.includes(filterValue.toLowerCase());
}

const columnHelper = createColumnHelper<RotationalProducts>();

type LinkedInstaller = {
  id: string;
  name: string;
  warehouse_info: {
    id: string | null;
    label: string | null;
    bin_location: string | null;
  };
};

interface Installer {
  supplier_id: string;
  name: string;
  email: string;
}

type AgentProduct = {
  product_id: string;
  agent_id: string;
  product: Product;
  created_by: {
    id: string;
    name: string;
  };
};

const SignageRotationalProducts = () => {
  const [rotationalProducts, setRotationalProducts] = useState<RotationalProducts[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null); // Track deletion in progress
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
  const [parentSignageId, setParentSignageId] = useState<string | null>(null);
  const [parentSignageName, setParentSignageName] = useState<string | null>(null);
  const [toastShown, setToastShown] = useState(false);
  const location = useLocation();

  // Determine the base route based on current location
  const isSignageTeammember = location.pathname.includes('/signageteammember');
  const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';

  const { canCreate, canView, canEdit, canDelete } = useModuleActionPermissions(
    MODULE_PERMISSION_NAMES.assets
  );
  const signageId = (isSignageTeammember && parentSignageId) ? parentSignageId : userId;
  const [installers, setInstallers] = useState<Installer[]>([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [conditionFilter, setConditionFilter] = useState("");
  const itemsPerPage = 10;
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [globalFilter, setGlobalFilter] = useState("");
  const [filteredProducts, setFilteredProducts] = useState<RotationalProducts[]>([]);
  const [exportRows, setExportRows] = useState<ExportRow[]>([]);
  const [searchInput, setSearchInput] = useState<string>(globalFilter ?? "");
  const [linkedStatusFilter, setLinkedStatusFilter] = useState("");
  const [currentStatusFilter, setCurrentStatusFilter] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  // useEffect(() => {
  //   const fetchInstallers = async () => {
  //     try {
  //       const response = await fetch(`${process.env.REACT_APP_API_URL}/agent/suppliers`, {
  //         method: "GET",
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //           "Content-Type": "application/json",
  //         },
  //       });

  //       if (!response.ok) {
  //         throw new Error("Failed to fetch installers data");
  //       }

  //       const data = await response.json();
  //       console.log("Fetched Installers:", data);

  //       if (Array.isArray(data)) {
  //         setInstallers(data);
  //       } else {
  //         console.error("Unexpected installers data format:", data);
  //       }
  //     } catch (error) {
  //       console.error("Error fetching installers data:", error);
  //     }
  //   };

  //   fetchInstallers();
  // }, [token]);

  useEffect(() => {
    if (isSignageTeammember && token) {
      fetchParentHierarchy(token).then((res) => {
        if (res) {
          setParentSignageId(res.parentSignageId);
          setParentSignageName(res.parentSignageName);
        }
      });
    }
  }, [isSignageTeammember, token]);

  useEffect(() => {
    const fetchRotationProducts = async () => {
      if (!signageId || !token) return;
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/signage/${signageId}/rotational-products/datatable`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              page: pagination.pageIndex + 1,
              limit: pagination.pageSize,
              search: globalFilter,
              filters: {
                status: linkedStatusFilter,
                condition: currentStatusFilter,
              },
              sort: {
                field: "created_at",
                order: "desc"
              },
              fields: ["id", "rotational_product", "total_allocated_quantity", "total_quantity", "created_at"]
            })
          }
        );

        const result = await response.json();
        console.log("Rotational Products:", result);
        setRotationalProducts(result?.data || []);
        setRowCount(result?.total || 0);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchRotationProducts();
  }, [token, signageId, globalFilter, linkedStatusFilter, currentStatusFilter, pagination]);

  const handleDeleteClick = (rotationalProduct: RotationalProducts) => {
    setDeleteTarget({
      id: rotationalProduct._id,
      name: rotationalProduct.rotational_product?.name || "Rotational Product",
    });
  };

  const handleDeleteCancel = () => {
    setDeleteTarget(null);
    setDeleting(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    await deleteRotationProduct(deleteTarget.id);
    setDeleteTarget(null);
  };

  const deleteRotationProduct = async (rotationalProductId: string) => {
    if (!signageId || !token) return;
    setDeleting(rotationalProductId);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/signage/${signageId}/rotational-products/${rotationalProductId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Delete API Error:", errorData);
        showError(errorData?.error || errorData?.message || "Failed to delete rotational product");
        return;
      }

      const result = await response.json();
      showSuccess(result?.message || "Rotational product deleted successfully");

      setRotationalProducts((prev) =>
        prev.filter((p) => p._id !== rotationalProductId)
      );
    } catch (error) {
      console.error("Fetch Error:", error);
      showError(error instanceof Error ? error.message : "An error occurred while deleting the product");
    } finally {
      setDeleting(null);
    }
  };

  const columns = useMemo(() => {
    return [
      columnHelper.display({
        id: "serial",
        header: () => (
          <div className="th-text S-No">S.No.</div>
        ),
        cell: (info) => (
          <div className="text-sm S-No" id={`cell-serial-${info.row.id}`}>
            {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
          </div>
        ),
      }),
      columnHelper.accessor(
        (row: RotationalProducts) => row?.rotational_product?.name,
        {
          id: "name",
          header: () => (
            <div className="th-text S-No-2">Product Name</div>
          ),
          cell: (info) => (
            <div className="text-sm S-No-2" id={`cell-name-${info.row.id}`}>
              {info.getValue()}
            </div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      columnHelper.accessor(
        (row: RotationalProducts) => row?.rotational_product?.category,
        {
          id: "category",
          header: () => (
            <div className="th-text">Product Category</div>
          ),
          cell: (info) => (
            <div className="text-sm" id={`cell-category-${info.row.id}`}>
              {info.getValue()}
            </div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      columnHelper.accessor(
        (row: RotationalProducts) => row?.total_allocated_quantity,
        {
          id: "total_allocated_quantity",
          header: () => (
            <div className="th-text">Tot-Allocated</div>
          ),
          cell: (info) => (
            <div className="text-sm" id={`cell-allocated-${info.row.id}`}>
              {info.getValue()}
            </div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      columnHelper.accessor(
        (row: RotationalProducts) => row?.total_quantity,
        {
          id: "total_quantity",
          header: () => (
            <div className="th-text">Tot-Available</div>
          ),
          cell: (info) => (
            <div className="text-sm" id={`cell-available-${info.row.id}`}>
              {info.getValue()}
            </div>
          ),
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      columnHelper.accessor("total_quantity",
        {
          id: "availability_status",
          header: () => (
            <div className="th-text">Status</div>
          ),
          cell: (info) => {
            const isAllocated = info.row.original.total_quantity > 0;
            return (
              <div className="text-sm" id={`cell-status-${info.row.id}`}>
                <span className={`status-badge ${isAllocated ? "status-accepted-btn" : "status-pending-btn"}`}>
                  {isAllocated ? "Available" : "Allocated"}
                </span>
              </div>
            );
          },
          enableGlobalFilter: true,
        }),
      columnHelper.accessor(
        (row: RotationalProducts) => row?.rotational_product?.item_condition,
        {
          id: "item_condition",
          header: () => (
            <div className="th-text">Condition</div>
          ),
          cell: (info) => {
            const value = info.getValue();
            let displayValue = value || "-";
            if (typeof value === "string" && value.length > 0) {
              displayValue = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
            }
            return (
              <div className="text-sm" id={`cell-condition-${info.row.id}`}>
                {displayValue}
              </div>
            );
          },
          filterFn: fuzzyTextFilter,
          enableGlobalFilter: true,
        }
      ),
      columnHelper.display({
        id: "actions",
        header: () => (
          <div className="th-text">Actions</div>
        ),
        cell: (info) => {
          const product = info.row.original;
          return (
            <div className="d-flex align-items-center gap-2" id={`cell-actions-${info.row.id}`}>
              {canView && (
              <ViewIconButton
                to={`${baseRoute}/editRotationalProduct/${product._id}`}
                className="btn action-btn fs-2 text-center"
                title="View"
                id={`action-view-${product._id}`}
              />
              )}

              {canEdit && (
              <EditIconButton
                to={`${baseRoute}/editRotationalProduct/${product._id}`}
                className="icon-square"
                title="Edit"
                id={`action-edit-${product._id}`}
              />
              )}
              {canDelete && (
              <DeleteIconButton
                className="icon-square danger"
                title="Delete"
                id={`action-delete-${product._id}`}
                onClick={() => handleDeleteClick(product)}
                disabled={deleting === product._id}
              />
              )}
            </div>
          );
        },
      }),
    ];
  }, [deleting, pagination, baseRoute, canView, canEdit, canDelete]);



  const table = useReactTable({
    data: rotationalProducts,
    columns,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    state: {
      pagination,
      globalFilter,
    },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });

  const pageCount = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex;

  return (
    <>
    <div className="content">
      <div className="d-flex justify-content-between align-items-center pb-3 mb-2 py-2">
        <h4 className="mb-0 "></h4>
        <div className="d-flex gap-3 align-items-center">
          {canCreate && (
            <Link to={`${baseRoute}/addRotationalProduct`} className="btn btn-primary  table-addbutton">
            + Add Assets Product
          </Link>
          )}
        </div>
      </div>
      <div className="po-header mb-4 shadow-border-datatables ">
        <div className="po-top d-flex justify-content-between align-items-start align-items-md-center flex-wrap gap-2">
          <div className="po-title-wrap me-2 d-flex align-items-center">
            <h4 className="po-title mb-0">Assets</h4>
          </div>

          {/* tools block (search + buttons) */}
          <div className="po-tools d-flex align-items-center flex-wrap gap-2 ms-auto">
            {/* Search */}
            <div className="input-group po-search ">
              <span className="input-group-text">
                <i className="ri-search-line" />
              </span>
              <input
                type="text"
                id="searchInput"
                name="searchInput"
                className="form-control"
                placeholder="Search by product Name,se......"
                value={searchInput}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchInput(e.target.value)}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Enter") {
                    setPagination((prev) => ({ ...prev, pageIndex: 0 })); // ✅ Reset to page 1
                    setGlobalFilter(searchInput);
                  }
                }}
                autoComplete="off"
              />
            </div>
            {/* <ExportAsExcel
                         data={exportRows}
                         headers={[
                           "Company Name",
                           "First Name",
                           "Last Name",
                           "Phone",
                           "Email",
                           "License Status",
                           "Status",
                         ]}
                         fileName="BrokerageFirms"
                       >
                         {(props) => (
                           <button {...props} className="export-button">
                             <i className="ri-file-excel-2-line me-2" />
                             Export to Excel
                           </button>
                         )}
                       </ExportAsExcel> */}
          </div>
        </div>
      </div>

      <div className="card overflow-hidden production-card box-shadow-datatables shadow-border-datatables">
        {/* Header like screenshot (left title, right count pill) */}
        <div className="align-items-center bg-white border-bottom  d-flex justify-content-between px-4 py-4">
          <h5 className="mb-0 "></h5>
          {/* <span className="badge rounded-pill bg-body-tertiary text-secondary-emphasis px-3 py-2">
                  orders
                </span> */}
        </div>
        <hr className="mb-2" style={{ borderTop: "none" }} />
        {/* DESKTOP (≥ 1022px) */}
        <div className="rdt-desktop ">
          <div
            className="production-scroll"
            style={{ "--rdt-min": "1200px" } as React.CSSProperties}
          >
            <table className="table production-table align-middle mb-0 table-1">
              <DesktopTableParts table={table} />
            </table>
          </div>
        </div>

        {/* MOBILE (≤ 1021px) */}
        <div className="rdt-mobile">
          <MobileCardsOnly
            table={table}
            idColId="order_link"
            titleColId="product_name"
            actionColId="actions"
          />
        </div>

      </div>
      {table.getRowModel().rows.length === 0 ? (
        <div className="text-center my-4 text-muted" style={{ fontSize: "1.1rem" }}>
          No records found
        </div>
      ) : (
        <div className="mt-4 mb-4 ">
          <TablePaginationControls table={table} />
        </div>
      )}
    </div>
    <DeleteConfirmModal
      open={!!deleteTarget}
      dynamicName={deleteTarget?.name || "Rotational Product"}
      onCancel={handleDeleteCancel}
      onDelete={handleDeleteConfirm}
    />
    </>
  );
};

export default SignageRotationalProducts;
